import { useMemo } from "react";
import { stringify, parse } from "qs";
import {
  UrlStateType,
  UrlSerializers,
  UrlSerializer,
  ToUrlQuery,
} from "@/lib/UrlSerializers";
import { useSearchParams } from "react-router-dom";

// generate data type for a given serializer based on it's UrlSerializers key
type SerializerDataTypes = {
  [K in UrlStateType]: (typeof UrlSerializers)[K] extends UrlSerializer<infer T>
    ? T
    : never;
};

// get data from url to use to initialize state on a page.
export const useUrlState = <K extends UrlStateType>(
  key: K,
): SerializerDataTypes[K] | undefined => {
  const [queryString] = useSearchParams();

  return useMemo(() => {
    const fromUrlQuery = UrlSerializers[key].fromUrlQuery;
    const parsedUrl = parseQueryString(queryString.toString());
    let data = undefined;
    try {
      data = fromUrlQuery(parsedUrl);
    } catch (e) {
      console.error(e);
    }
    return data as SerializerDataTypes[typeof key];
  }, [queryString, key]);
};

// serialize some data before passing it to useRoute to build a route
export const useBuildUrlState = <K extends UrlStateType>(key: K) => {
  return useMemo(() => buildUrlState(key), [key]);
};

export const buildUrlState = <K extends UrlStateType>(key: K) => {
  return UrlSerializers[key].toUrlQuery as ToUrlQuery<SerializerDataTypes[K]>;
};

// convert serialized data object to final query string.
export const convertToQueryString = (data: object) => {
  // TODO: compress query string here...
  return stringify(data, { skipNulls: true });
};

// accept query string and convert it js object
export const parseQueryString = (queryString: string) => {
  // TODO: decompress query string here...
  return parse(queryString.replace(/^\?/, ""));
};
